import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { FaAngleLeft, FaAngleRight, FaBroadcastTower, FaCog, FaLayerGroup, FaSignOutAlt, FaUsers } from "react-icons/fa";
import { api, setActiveOrg, type AuthState } from "./api.ts";
import { CreateOrgForm } from "./components/CreateOrgForm.tsx";
import { Select } from "./components/Select.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { LiveDetectionPage } from "./pages/LiveDetectionPage.tsx";
import { ProjectPage } from "./pages/ProjectPage.tsx";
import { SettingsPage } from "./pages/SettingsPage.tsx";
import { TeamPage } from "./pages/TeamPage.tsx";
import { CreateOrganization, ForgotPassword, Login, ResetPassword, Signup } from "./pages/AuthPages.tsx";
import { PublicDemoPage } from "./pages/PublicDemoPage.tsx";

// The landing page and docs are served by the Astro marketing site at "/" and
// "/docs"; this SPA is mounted at /app and only handles authenticated routes.
type Route = { page: "login"|"signup"|"forgot"|"reset"|"onboarding"|"org"; org?: string; project?: string; section?: "team"|"offline_projects"|"live"|"settings" };
function route(): Route {
  const h=location.hash.replace(/^#/,"");
  if(h.startsWith("/docs")){location.replace("/docs");return{page:"login"}}
  if(h==="/login")return{page:"login"}; if(h==="/signup")return{page:"signup"}; if(h==="/forgot-password")return{page:"forgot"};
  if(h.startsWith("/reset-password"))return{page:"reset"};
  if(h==="/onboarding")return{page:"onboarding"};
  const p=h.match(/^\/o\/([^/]+)(?:\/(project)\/(.+)|\/(team)|\/(offline_projects)|\/(live)|\/(settings))?$/);
  if(p)return{page:"org",org:decodeURIComponent(p[1]),project:p[2]?decodeURIComponent(p[3]):undefined,section:p[4]?"team":p[5]?"offline_projects":p[6]?"live":p[7]?"settings":undefined};
  return{page:"login"};
}
const go=(path:string)=>{location.hash=path};

export function App() {
  const [r,setRoute]=useState(route()),[auth,setAuth]=useState<AuthState|null>(null),[checked,setChecked]=useState(false);
  const [collapsed,setCollapsed]=useState(()=>localStorage.getItem("sidebar-collapsed")==="1");
  const toggleCollapsed=()=>setCollapsed(c=>{localStorage.setItem("sidebar-collapsed",c?"0":"1");return !c});
  useEffect(()=>{const fn=()=>setRoute(route());addEventListener("hashchange",fn);if(location.pathname==="/demo"){setChecked(true);return()=>removeEventListener("hashchange",fn)}api.me().then(setAuth).catch(()=>{}).finally(()=>setChecked(true));return()=>removeEventListener("hashchange",fn)},[]);
  const accepted=(state:AuthState)=>{setAuth(state);if(state.organizations.length===0)go("/onboarding");else go(`/o/${state.organizations[0].slug}/offline_projects`)};

  if(location.pathname==="/demo") return <PublicDemoPage/>;
  if(r.page==="login") return <Login onAuth={accepted}/>;
  if(r.page==="signup") return <Signup onAuth={accepted}/>;
  if(r.page==="forgot") return <ForgotPassword/>;
  if(r.page==="reset") return <ResetPassword/>;
  if(!checked) return <div className="screen-loader"><span/></div>;
  if(!auth) { go("/login"); return null; }
  if(r.page!=="org" || auth.organizations.length===0) return <CreateOrganization onCreated={async slug=>{setAuth(await api.me());go(`/o/${slug}/offline_projects`)}}/>;
  const active=auth.organizations.find(o=>o.slug===r.org);
  if(!active) { go(auth.organizations.length===1?`/o/${auth.organizations[0].slug}/offline_projects`:"/onboarding"); return null; }
  if(!r.project&&!r.section) { go(`/o/${active.slug}/offline_projects`); return null; }
  setActiveOrg(active.slug);
  const navTip=(content:string)=>collapsed?{"data-tooltip-id":"sidebar-tip","data-tooltip-content":content}:{};

  return <div className={`app-shell${collapsed?" sidebar-collapsed":""}`}>
    <aside className="sidebar">
      <button
        className="sidebar-toggle"
        aria-label={collapsed?"Expand sidebar":"Collapse sidebar"}
        data-tooltip-id="sidebar-tip"
        data-tooltip-content={collapsed?"Expand sidebar":"Collapse sidebar"}
        onClick={toggleCollapsed}
      >{collapsed?<FaAngleRight size={11}/>:<FaAngleLeft size={11}/>}</button>
      <a className="brand" href="/">
        <span className="sidebar-logo">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 12h-2c-.894 0 -1.662 -.857 -1.761 -2c-.296 -3.45 -.749 -6 -2.749 -6s-2.5 3.582 -2.5 8s-.5 8 -2.5 8s-2.452 -2.547 -2.749 -6c-.1 -1.147 -.867 -2 -1.763 -2h-2" stroke="#0e0f18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <span className="brand-word">Signals</span>
      </a>
      <div className="sidebar-org">
        <div className="org-label">ORGANIZATION</div>
        <Select
          value={active.slug}
          onChange={slug=>go(`/o/${slug}/offline_projects`)}
          options={auth.organizations.map(o=>({value:o.slug,label:o.name}))}
          footer={close=><CreateOrgForm onCreated={async slug=>{setAuth(await api.me());go(`/o/${slug}/offline_projects`);close()}}/>}
        />
      </div>
      <nav>
        <a className={`nav-item${r.section==="offline_projects"?" active":""}`} href={`#/o/${active.slug}/offline_projects`} aria-label="Offline Sdk" {...navTip("Offline Sdk")}>
          <span className="nav-icon"><FaLayerGroup size={13}/></span><span className="nav-label">Offline Sdk</span>
        </a>
        <a className={`nav-item${r.section==="live"?" active":""}`} href={`#/o/${active.slug}/live`} aria-label="Live Detection (Beta)" {...navTip("Live Detection (Beta)")}>
          <span className="nav-icon"><FaBroadcastTower size={15}/></span><span className="nav-label">Live Detection<span className="beta-badge">Beta</span></span>
        </a>
        <a className={`nav-item${r.section==="team"?" active":""}`} href={`#/o/${active.slug}/team`} aria-label="Team" {...navTip("Team")}>
          <span className="nav-icon"><FaUsers size={15}/></span><span className="nav-label">Team</span>
        </a>
        <a className={`nav-item${r.section==="settings"?" active":""}`} href={`#/o/${active.slug}/settings`} aria-label="Settings" {...navTip("Settings")}>
          <span className="nav-icon"><FaCog size={13}/></span><span className="nav-label">Settings</span>
        </a>
      </nav>
      <div className="sidebar-user">
        {auth.user.avatarUrl?<img src={auth.user.avatarUrl} alt="" referrerPolicy="no-referrer"/>:<div>{auth.user.name.slice(0,1).toUpperCase()}</div>}
        <span><b>{auth.user.name}</b><small className="role-pill">{active.role}</small></span>
        <button className="logout-btn" aria-label="Sign out" data-tooltip-id="sidebar-tip" data-tooltip-content="Sign out" onClick={async()=>{await api.logout();setAuth(null);go("/login")}}><FaSignOutAlt size={13}/></button>
      </div>
      <Tooltip
        id="sidebar-tip"
        place="right"
        className="app-tooltip"
        style={{backgroundColor:"#14151d",color:"#e2e2ea",padding:"6px 10px",borderRadius:7,fontSize:12,opacity:1,zIndex:50,border:"1px solid #292b38",boxShadow:"0 10px 30px rgba(0,0,0,.6)"}}
      />
    </aside>
    <section className="app-main"><header className="topbar">
      <div className="breadcrumb">
        <a href={`#/o/${active.slug}/offline_projects`}>{active.name}</a>
        <span className="crumb-sep">/</span>
        {r.project ? (
          <>
            <a href={`#/o/${active.slug}/offline_projects`}>Offline Sdk</a>
            <span className="crumb-sep">/</span>
            <span className="crumb-current">{r.project}</span>
          </>
        ) : (
          <span className="crumb-current">{r.section==="team"?"Team":r.section==="live"?"Live Detection":r.section==="settings"?"Settings":"Offline Sdk"}</span>
        )}
      </div>
    </header><main>
      {r.project?<ProjectPage name={r.project} orgSlug={active.slug}/>
        :r.section==="team"?<TeamPage currentUserId={auth.user.id} currentRole={active.role}/>
        :r.section==="live"?<LiveDetectionPage/>
        :r.section==="settings"?<SettingsPage orgName={active.name} currentRole={active.role} onOrgDeleted={async()=>{const state=await api.me();setAuth(state);if(state.organizations.length===0)go("/onboarding");else go(`/o/${state.organizations[0].slug}/offline_projects`)}}/>
        :<Dashboard orgSlug={active.slug}/>}
    </main></section>
  </div>;
}
